<script lang="ts" setup>
import { computed } from 'vue';
import SuspendedErrorHandler from '@/components/suspense/SuspendedErrorHandler.vue';
import { useI18n } from 'vue-i18n';
import SvgIllustration from '@/components/SvgIllustrations/SvgIllustration.vue';
import useAppearance from '@/composables/useAppearance';
import type { ISettingsEvents } from '@/types/components/settings/folders/Settings';
import { useRoute } from 'vue-router';
import { ROUTE_NAMES } from '@/router/conf';
import { EAppearanceField } from '@/types/composables/useAppearance.enums';
import type {
  IAppearanceSettingsForm,
  IAppearanceSettingsGroup
} from '@/types/composables/useAppearance';
import { getEnabledThemesObjects } from '@/helpers/theme';
import useChangeLocale from '@/composables/useChangeLocale';
import useUserService from '@/composables/apiServices/useUserService';

const { t, locale } = useI18n();
const route = useRoute();

const emit = defineEmits<ISettingsEvents>();
const { details, updateDetails } = useUserService();
const { data: userDetails } = details();
const { mutateAsync: updateUserDetails } = updateDetails();
const { handleDetailsChange: handleChange, detailsForm } = useAppearance();
const { languageOptions } = useChangeLocale();

const handleViewAllSettings = async () => {
  emit('close');
};

const language = computed<string>({
  get: () => {
    return userDetails.value?.language || locale.value;
  },
  set: async (value: string) => {
    await updateUserDetails({
      language: value
    });
  }
});

const settingsGroups = computed((): IAppearanceSettingsGroup<keyof IAppearanceSettingsForm>[] => [
  {
    key: EAppearanceField.DISPLAY_DENSITY,
    groupLabel: t('settings.appearance.displayDensity.label'),
    model: detailsForm.value.displayDensity,
    options: [
      {
        label: t('settings.appearance.displayDensity.options.default'),
        value: 'default',
        image: 'DensityDefault'
      },
      {
        label: t('settings.appearance.displayDensity.options.comfortable'),
        value: 'comfortable',
        image: 'DensityComfortable'
      },
      {
        label: t('settings.appearance.displayDensity.options.compact'),
        value: 'compact',
        image: 'DensityCompact'
      }
    ]
  },
  {
    key: EAppearanceField.READING_PANE,
    groupLabel: t('settings.appearance.readingPane.label'),
    model: detailsForm.value.readingPane,
    options: [
      {
        label: t('settings.appearance.readingPane.options.right'),
        value: '3-panel',
        image: 'PaneRightImage'
      },
      {
        label: t('settings.appearance.readingPane.options.bottom'),
        value: '3-panel-bottom',
        image: 'PaneBottomImage'
      },
      {
        label: t('settings.appearance.readingPane.options.hide'),
        value: '2-panel',
        image: 'PaneHideImage'
      }
    ]
  },
  //  These options will be added later in the future
  // {
  //   key: EAppearanceField.INBOX_TYPE,
  //   groupLabel: t('settings.appearance.inboxType.label'),
  //   model: detailsForm.value.inboxType,
  //   options: [
  //     {
  //       label: t('settings.appearance.inboxType.options.default'),
  //       value: 'default',
  //       image: 'InboxTypeDefault'
  //     },
  //     {
  //       label: t('settings.appearance.inboxType.options.unreadFirst'),
  //       value: 'unread-first',
  //       image: 'UnreadFirst'
  //     }
  // These options will be added later in the future
  // {
  //   label: t('settings.inboxType.options.starredFirst'),
  //   value: 'starred-first',
  //   image: 'StarredFirst'
  // },
  //   {
  //     label: t('settings.inboxType.options.readFirst'),
  //     value: 'read-first',
  //     image: 'ReadFirst'
  //   }
  //   ]
  // },
  {
    key: EAppearanceField.SELECTED_COLOR,
    groupLabel: t('settings.appearance.color.title'),
    model: detailsForm.value.selectedColor,
    options: getEnabledThemesObjects(t)
  }
]);
</script>

<template>
  <div class="m-4 quick-settings" sentry-unmask>
    <SuspendedErrorHandler>
      <v-row class="d-flex align-center justify-space-between">
        <v-col cols="auto">
          <h3 class="text-title-lg px-2">{{ t('settings.label') }}</h3>
        </v-col>
        <v-col cols="auto">
          <CloseBtn
            class="close-btn"
            color="on-surface"
            data-test-id="quick-settings-close"
            icon
            @click="emit('close')"
          >
            <v-icon>mdi-close</v-icon>
          </CloseBtn>
        </v-col>
      </v-row>
      <outlined-btn
        class="my-2 quick-settings__view-all"
        width="100%"
        @click="handleViewAllSettings"
        :to="{
          name: ROUTE_NAMES.SETTINGS_GENERAL,
          params: {
            userIndex: route.params.userIndex
          }
        }"
        data-onboarding="quick-settings-view-all"
      >
        <span class="text-body-lg">
          {{ t('settings.viewAllSettings') }}
        </span>
      </outlined-btn>
      <div class="d-flex align-center justify-space-between">
        <v-label class="cursor-pointer text-label-lg on-surface-variant" for="dark-mode-switch">
          {{ t('settings.appearance.theme.options.darkMode') }}
        </v-label>
        <v-switch
          id="dark-mode-switch"
          :model-value="detailsForm.darkMode"
          :false-value="'light'"
          :true-value="'dark'"
          hide-details
          @change="
            () =>
              handleChange(
                EAppearanceField.DARK_MODE,
                detailsForm.darkMode === 'dark' ? 'light' : 'dark'
              )
          "
        />
      </div>

      <v-select
        v-model="language"
        :items="languageOptions"
        :label="t('settings.general.language')"
        data-test-id="quick-settings-language"
        hide-details
        variant="outlined"
        class="mb-4"
        sentry-unmask
      />

      <div v-for="group in settingsGroups" :key="group.key" class="w100 radio-group">
        <v-label class="text-label-lg on-surface-variant py-4">
          {{ group.groupLabel }}
        </v-label>
        <v-radio-group :model-value="group.model" hide-details>
          <v-radio
            v-for="option in group.options"
            :key="option.value"
            :value="option.value"
            class="py-2"
            @change="handleChange(group.key, option.value)"
            :data-onboarding="'quick-settings-' + option.value"
          >
            <template #label>
              <div class="d-flex align-center justify-space-between w-100">
                <span>{{ option.label }}</span>
                <SvgIllustration v-if="option.image" :name="option.image" />
                <div
                  v-if="option.color"
                  :style="{ backgroundColor: option.color }"
                  class="color-circle"
                ></div>
              </div>
            </template>
          </v-radio>
        </v-radio-group>
      </div>
    </SuspendedErrorHandler>
  </div>
</template>

<style lang="scss">
.quick-settings {
  background: rgb(var(--v-theme-quick-settings));
  padding: 0.75rem;
  border-radius: 0.375rem;
  min-height: 100%;

  .radio-group {
    border-bottom: 1px solid rgb(var(--v-theme-outline-variant));

    &:last-of-type {
      border-bottom: none;
    }

    .v-label {
      width: 100%;
    }
  }

  .color-circle {
    width: 2.1875rem;
    height: 2.1875rem;
    border-radius: 50%;
  }

  &__view-all {
    .text-body-lg {
      font-weight: 500 !important;
    }
  }
}
</style>
